import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, User, X, Filter, RotateCcw, Eye } from "lucide-react";
import { Dialog } from "@headlessui/react";
import { getSmartAnalyticsCandidates } from "../../Services/SmartAnalyticsService";
import {
  CANDIDATE_AVAILABILITY_OPTIONS,
  CANDIDATE_RECOMMENDATION_OPTIONS,
  PROFILE_COMPLETE_OPTIONS,
  SmartAnalyticsCandidateFilters,
  SmartAnalyticsCandidateRow,
  USER_STATUS_OPTIONS,
} from "../../Types/SmartAnalyticsType";
import Pagination from "../../../Shared/Pagination/Pagination";
import { PaginationResponse } from "../../../Shared/Pagination/PaginationResponse";
import { formatDateTime } from "../../../Shared/Utils/HelperUtils";
import { EmptyState, fieldClass, LoadingRows, PAGE_SIZE, statusBadge } from "./SmartAnalyticsShared";
import CandidateProfileView from "../../../Candidate/Components/CandidateProfileView";
import { getCandidateById, getInvitationStatsByCandidiateId } from "../../../Candidate/Services/CandidateService";
import { CandidateInvitationStats, CandidateProfile } from "../../../Candidate/Types/CandidateType";

interface LocalFilters {
  search: string;
  status: string;
  availabilityStatus: string;
  profileComplete: string;
  minScore: string;
  maxScore: string;
  minExperience: string;
  maxExperience: string;
  recommendation: string;
}

const DEFAULT_FILTERS: LocalFilters = {
  search: "",
  status: "",
  availabilityStatus: "",
  profileComplete: "",
  minScore: "",
  maxScore: "",
  minExperience: "",
  maxExperience: "",
  recommendation: "",
};

const toNumber = (value: string): number | undefined => {
  const parsed = value.trim() === "" ? NaN : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildApiFilters = (local: LocalFilters): SmartAnalyticsCandidateFilters => {
  const result: SmartAnalyticsCandidateFilters = {};
  if (local.search) result.search = local.search;
  if (local.status) result.status = local.status;
  if (local.availabilityStatus) result.availabilityStatus = local.availabilityStatus;
  if (local.profileComplete) result.profileComplete = local.profileComplete;
  if (local.recommendation) result.recommendation = local.recommendation;

  const minScore = toNumber(local.minScore);
  const maxScore = toNumber(local.maxScore);
  const minExperience = toNumber(local.minExperience);
  const maxExperience = toNumber(local.maxExperience);

  if (minScore !== undefined) result.minScore = minScore;
  if (maxScore !== undefined) result.maxScore = maxScore;
  if (minExperience !== undefined) result.minExperience = minExperience;
  if (maxExperience !== undefined) result.maxExperience = maxExperience;

  return result;
};

const toUuid = (id: string): string => {
  const hex = (id || "").replace(/-/g, "").trim();
  if (hex.length !== 32) return id;
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
};

const scoreClass = (score: number | null | undefined) => {
  const value = score ?? 0;
  if (value >= 8) return "text-emerald-400";
  if (value >= 6) return "text-cyan-400";
  if (value >= 4) return "text-amber-400";
  return "text-rose-400";
};

const computeRecommendations = (candidate: SmartAnalyticsCandidateRow): string[] => {
  const items: string[] = [];
  const score = candidate.overallScore ?? 0;
  const completeScore = candidate.profileCompleteScore ?? 0;

  if (candidate.receivedInvitationCount === 0) {
    items.push("No invitations yet — consider profile visibility or outreach");
  }
  if (score < 6 || completeScore < 60) {
    items.push("Low profile score — strengthen headline, achievements & scores");
  }
  if (!candidate.profileComplete) {
    items.push("Incomplete profile — prompt candidate to finish onboarding");
  }
  if (candidate.linkedinVerified === 0 && candidate.workEmailVerified === 0 && candidate.phoneVerified === 0) {
    items.push("No verifications — ask to verify LinkedIn, email or phone");
  }
  if (candidate.lastActivityAt) {
    const days = (Date.now() - new Date(candidate.lastActivityAt).getTime()) / (1000 * 60 * 60 * 24);
    if (days > 7) {
      items.push("No recent activity — re-engage with relevant opportunities");
    }
  }

  return items;
};

const CandidateDetailModal = ({
  userId,
  onClose,
}: {
  userId: string | null;
  onClose: () => void;
}) => {
  const [profile, setProfile] = useState<CandidateProfile | null>(null);
  const [stats, setStats] = useState<CandidateInvitationStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      setStats(null);
      return;
    }
    const uuid = toUuid(userId);
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getCandidateById(uuid),
      getInvitationStatsByCandidiateId(uuid),
    ])
      .then(([profileRes, statsRes]) => {
        if (cancelled) return;
        setProfile(profileRes?.data ?? null);
        setStats(statsRes?.data ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <Dialog open={!!userId} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
            <Dialog.Title className="text-sm font-black uppercase tracking-widest text-slate-100">
              Candidate dashboard
            </Dialog.Title>
            <button
              onClick={onClose}
              className="rounded-full border border-slate-800 bg-slate-900 p-2 text-slate-400 transition hover:text-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[calc(90vh-80px)] overflow-y-auto p-2">
            {loading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-500">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-700 border-t-cyan-400" />
                <p className="text-xs font-bold uppercase tracking-widest">Loading candidate data...</p>
              </div>
            ) : profile ? (
              <>
                {stats && (
                  <div className="mb-4 grid grid-cols-1 gap-3 px-2 sm:grid-cols-3">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Invitations received</p>
                      <p className="mt-1 text-2xl font-black text-cyan-400">{stats.receivedInvitationCount ?? 0}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Accepted</p>
                      <p className="mt-1 text-2xl font-black text-emerald-400">{stats.acceptedInvitationCount ?? 0}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rejected</p>
                      <p className="mt-1 text-2xl font-black text-amber-400">{stats.rejectedInvitationCount ?? 0}</p>
                    </div>
                  </div>
                )}
                <CandidateProfileView data={profile} />
              </>
            ) : (
              <EmptyState
                icon={<User className="h-6 w-6" />}
                title="Candidate not found"
                body="We couldn't load the full profile for this candidate."
              />
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

const SmartAnalyticsCandidatesTab = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<PaginationResponse<SmartAnalyticsCandidateRow> | null>(null);
  const [filters, setFilters] = useState<LocalFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<LocalFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const hasFilters = useMemo(
    () => Object.values(appliedFilters).some((value) => value !== ""),
    [appliedFilters]
  );

  const load = useCallback(async () => {
    const response = await getSmartAnalyticsCandidates(buildApiFilters(appliedFilters), page, PAGE_SIZE);
    if (response.success) {
      setCandidates(response.data);
    }
  }, [appliedFilters, page]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPage(0);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  const updateFilter = (key: keyof LocalFilters, value: string) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
              <Users className="h-4 w-4 text-cyan-400" />
              Candidates
            </h2>
            <p className="mt-1 text-[10px] font-semibold text-slate-500">
              {candidates?.totalElements ?? 0} registered candidates
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasFilters && (
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-400 transition hover:text-slate-100"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            )}
            <button
              onClick={applyFilters}
              className="inline-flex items-center gap-1.5 rounded-xl bg-cyan-500/10 px-4 py-2 text-xs font-black uppercase tracking-wider text-cyan-400 transition hover:bg-cyan-500/20"
            >
              <Filter className="h-3.5 w-3.5" />
              Apply filters
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
            <input
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && applyFilters()}
              placeholder="Search name, email, city, company, role"
              className={`${fieldClass} pl-9`}
            />
          </div>

          <select
            value={filters.status}
            onChange={(event) => updateFilter("status", event.target.value)}
            className={fieldClass}
          >
            <option value="">All statuses</option>
            {USER_STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option.replace(/_/g, " ").toLowerCase()}
              </option>
            ))}
          </select>

          <select
            value={filters.availabilityStatus}
            onChange={(event) => updateFilter("availabilityStatus", event.target.value)}
            className={fieldClass}
          >
            <option value="">All availability</option>
            {CANDIDATE_AVAILABILITY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option.replace(/_/g, " ").toLowerCase()}
              </option>
            ))}
          </select>

          <select
            value={filters.profileComplete}
            onChange={(event) => updateFilter("profileComplete", event.target.value)}
            className={fieldClass}
          >
            {PROFILE_COMPLETE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={filters.recommendation}
            onChange={(event) => updateFilter("recommendation", event.target.value)}
            className={fieldClass}
          >
            {CANDIDATE_RECOMMENDATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={10}
              step={0.1}
              value={filters.minScore}
              onChange={(event) => updateFilter("minScore", event.target.value)}
              placeholder="Min score"
              className={fieldClass}
            />
            <span className="text-slate-600">-</span>
            <input
              type="number"
              min={0}
              max={10}
              step={0.1}
              value={filters.maxScore}
              onChange={(event) => updateFilter("maxScore", event.target.value)}
              placeholder="Max score"
              className={fieldClass}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={filters.minExperience}
              onChange={(event) => updateFilter("minExperience", event.target.value)}
              placeholder="Min exp (years)"
              className={fieldClass}
            />
            <span className="text-slate-600">-</span>
            <input
              type="number"
              min={0}
              value={filters.maxExperience}
              onChange={(event) => updateFilter("maxExperience", event.target.value)}
              placeholder="Max exp (years)"
              className={fieldClass}
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40">
        {loading ? (
          <table className="w-full">
            <tbody>
              <LoadingRows rows={4} cols={7} />
            </tbody>
          </table>
        ) : !candidates || candidates.content.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="No candidates match"
            body="Try changing the search or filters."
          />
        ) : (
          <table className="w-full">
            <thead className="border-y border-slate-800 bg-slate-900/60">
              <tr>
                <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Candidate</th>
                <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Role / Company</th>
                <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Invitations</th>
                <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Scores</th>
                <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Insights</th>
                <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.content.map((candidate) => {
                const recommendations = computeRecommendations(candidate);
                return (
                  <tr
                    key={candidate.userId}
                    onClick={() => navigate(`/admin/smart-analytics/candidate/${candidate.userId}`)}
                    className="cursor-pointer border-t border-slate-800/70 transition hover:bg-slate-900/40"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-800 bg-slate-950">
                          {candidate.profilePhotoUrl ? (
                            <img src={candidate.profilePhotoUrl} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <User className="h-5 w-5 text-slate-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-200">{candidate.fullName || candidate.email}</p>
                          <p className="text-[10px] font-semibold text-slate-500">{candidate.email}</p>
                          {candidate.city && <p className="text-[10px] font-semibold text-slate-600">{candidate.city}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col items-start gap-1.5">
                        {statusBadge(candidate.userStatus)}
                        {candidate.availabilityStatus && (
                          <span className="text-[10px] font-semibold text-slate-500">
                            {candidate.availabilityStatus.replace(/_/g, " ").toLowerCase()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-xs font-semibold text-slate-200">{candidate.currentRole || "-"}</p>
                      <p className="text-[10px] font-semibold text-slate-500">{candidate.currentCompany || "-"}</p>
                      <p className="text-[10px] font-semibold text-slate-600">
                        {candidate.totalYearsOfExperience ?? "-"} years · {candidate.roleLevel || "-"}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-3 text-xs font-black">
                        <span className="text-cyan-400" title="Received">{candidate.receivedInvitationCount}</span>
                        <span className="text-emerald-400" title="Accepted">{candidate.acceptedInvitationCount}</span>
                        <span className="text-amber-400" title="Rejected">{candidate.rejectedInvitationCount}</span>
                      </div>
                      <p className="text-[10px] font-semibold text-slate-500">invitations</p>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className={`text-lg font-black ${scoreClass(candidate.overallScore)}`}>
                            {candidate.overallScore?.toFixed(1) ?? "-"}
                          </p>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">overall</p>
                        </div>
                        <div className="h-8 w-px bg-slate-800" />
                        <div>
                          <p className="text-sm font-black text-slate-200">
                            {candidate.profileCompleteScore != null ? `${Math.round(candidate.profileCompleteScore)}%` : "-"}
                          </p>
                          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">profile</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      {recommendations.length > 0 ? (
                        <ul className="max-w-[220px] space-y-1">
                          {recommendations.slice(0, 2).map((item, index) => (
                            <li key={index} className="truncate text-[10px] font-semibold text-amber-400" title={item}>
                              * {item}
                            </li>
                          ))}
                          {recommendations.length > 2 && (
                            <li className="text-[10px] font-semibold text-slate-500">+{recommendations.length - 2} more</li>
                          )}
                        </ul>
                      ) : (
                        <span className="text-[10px] font-semibold text-slate-500">-</span>
                      )}
                      <p className="mt-2 text-[10px] font-semibold text-slate-600">
                        Last active {formatDateTime(candidate.lastActivityAt ?? undefined)}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedUserId(candidate.userId);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs font-black uppercase tracking-wider text-cyan-400 transition hover:bg-cyan-500/20"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {candidates && candidates.totalPages > 1 && (
          <div className="border-t border-slate-800 p-4">
            <Pagination {...candidates} setPage={setPage} />
          </div>
        )}
      </div>

      <CandidateDetailModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
    </div>
  );
};

export default SmartAnalyticsCandidatesTab;
